/**
 * The two moments where the app is *deliberately* between identities.
 *
 * Both of them look, to a route guard, exactly like a session that has just
 * expired — and a guard that treats them that way produces the two defects
 * Ivy ruled on in design spec §14.1:
 *
 *  - **`converting`** (requirement 2, LOCKED). A successful conversion revokes
 *    the guest session, so the very next request 401s. That 401 is *expected*.
 *    While this is set, the session refetch stands down and every guard freezes
 *    the route at its last render, so the page under the sheet never visibly
 *    navigates. Quinn saw the lobby slide to `/login?next=%2Flobby` behind a
 *    "check your email" panel; the redirect was not wrong, the redirect
 *    happening *without being asked for, underneath a modal* was.
 *  - **`logging-out`** (§14.1 ruling C, LOCKED). `?next=` records interruptions
 *    the system imposed, never departures the player chose. A guard that fires
 *    while a log-out is in flight would capture the screen the player is
 *    leaving on purpose and hand it back after the next log-in. While this is
 *    set, guards redirect with `next = null` — bare `/login`, every time,
 *    whether the guard or the screen's own `navigate` gets there first.
 *
 * It is a module-level store rather than React state because the auth client
 * sets it (§14.1 r2: "the auth client sets a `justConverted` flag before
 * applying the response"), and `lib/` must not depend on a provider. React
 * subscribes through `useSyncExternalStore` in `SessionProvider`.
 */

export type AuthTransition = "none" | "converting" | "logging-out";

let current: AuthTransition = "none";
const listeners = new Set<() => void>();

export function getAuthTransition(): AuthTransition {
  return current;
}

export function setAuthTransition(next: AuthTransition): void {
  if (current === next) return;
  current = next;
  for (const listener of listeners) listener();
}

export function subscribeAuthTransition(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Test-only reset — a module-level store outlives a `cleanup()`, and a test
 * that starts mid-conversion because the previous one ended there is a test
 * that fails for the wrong reason. Listeners are left alone; they belong to
 * whatever is currently mounted.
 */
export function resetAuthTransition(): void {
  current = "none";
}
